import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { formatTimeAgo } from "@/lib/dateFormat";
import { usePaginatedData, type PaginatedResponse } from "@/hooks/usePaginatedData";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronRight,
  Eye,
  PlugIcon,
  Pencil,
  Plus,
  RefreshCw,
  Loader2,
  Database,
  Palette,
  Zap,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  PlugZap,
  ShieldAlert,
  Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { SearchInput } from "@/components/ui/search-input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Pagination } from "@/components/ui/pagination";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import {
  fetchAdminConnectorTypes,
  updateConnectorType,
  createConnectorType,
  fetchAdminConnectorInstances,
  fetchGlobalConfig,
  updateGlobalConfig,
  fetchRunningConnectors,
  fetchConnectorRunHealth,
  fetchRegisteredSourceConnectorTypes,
  fetchConnectorTypeRecords,
  fetchLicenceModels,
  type AdminConnectorType,
  type AdminConnectorInstance,
  type ConnectorTypeUpdateRequest,
  type ConnectorTypeCreateRequest,
  type ConnectorTemplateRecord,
  type LicenceModel,
  type RegisteredSourceConnectorType,
  type RunningConnector,
  type ConnectorRunHealth,
} from "@/lib/portalData";
import { MappingHealthCard } from "@/components/portal/MappingHealthCard";
import { PromoteToTypeDialog } from "@/components/portal/connectors/PromoteToTypeDialog";
import { ConnectorRunHealthCard } from "@/components/portal/connectors/ConnectorRunHealthCard";
import { isPromotableTransport } from "@/components/portal/connectors/promotable";
import { ConnectionCell } from "@/components/portal/connectors/ConnectionCell";
import { platformApi, type Subscription } from "@/services/api/platform";

export const Route = createFileRoute("/portal/platform/connectors")({
  component: ConnectorsAdminPage,
});

function ColourSwatch({ hex }: { hex?: string }) {
  if (!hex) {
    return (
      <div className="h-6 w-6 rounded border border-dashed border-slate-300 bg-slate-50" />
    );
  }
  return (
    <div
      className="h-6 w-6 rounded border border-slate-200"
      style={{ backgroundColor: hex }}
      title={hex}
    />
  );
}

// ============================================================================
// Global Configuration Section
// ============================================================================

function GlobalConfigSection({
  token,
  onSelectConnector,
}: {
  token: string | null;
  onSelectConnector?: (connectorName: string) => void;
}) {
  const [disableConnectors, setDisableConnectors] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [runningConnectors, setRunningConnectors] = useState<RunningConnector[]>([]);
  const [runningLoading, setRunningLoading] = useState(true);
  const [runHealth, setRunHealth] = useState<ConnectorRunHealth | null>(null);
  const [runHealthLoading, setRunHealthLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const healthIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load global config
  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetchGlobalConfig(token)
      .then((items) => {
        const dc = items.find((i) => i.Key === "DisableConnectors");
        setDisableConnectors(dc?.Value === "true");
      })
      .catch(() => toast.error("Failed to load global configuration"))
      .finally(() => setLoading(false));
  }, [token]);

  // Load running connectors + auto-refresh every 30s
  const loadRunning = useCallback(async () => {
    if (!token) return;
    try {
      const connectors = await fetchRunningConnectors(token);
      setRunningConnectors(connectors);
    } catch {
      // Silently fail on refresh — don't spam toasts
    } finally {
      setRunningLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadRunning();
    intervalRef.current = setInterval(loadRunning, 30_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [loadRunning]);

  // Load connector RUN health (failing / overdue) + auto-refresh every 60s.
  // This is the run-OUTCOME signal — the class of failure previously only ever
  // found by accident. Slower cadence than running connectors; state changes on
  // the connector-run cycle, not second-to-second.
  const loadRunHealth = useCallback(async () => {
    if (!token) return;
    try {
      setRunHealth(await fetchConnectorRunHealth(token));
    } catch {
      // Silently fail on refresh — don't spam toasts
    } finally {
      setRunHealthLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadRunHealth();
    healthIntervalRef.current = setInterval(loadRunHealth, 60_000);
    return () => {
      if (healthIntervalRef.current) clearInterval(healthIntervalRef.current);
    };
  }, [loadRunHealth]);

  const handleToggle = async (checked: boolean) => {
    if (!token) return;
    setToggling(true);
    try {
      await updateGlobalConfig(token, "DisableConnectors", checked ? "true" : "false");
      setDisableConnectors(checked);
      toast.success(
        checked
          ? "Connectors disabled — new syncs will be blocked"
          : "Connectors enabled — syncs will resume",
      );
    } catch {
      toast.error("Failed to update configuration");
    } finally {
      setToggling(false);
    }
  };



  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Disable Connectors Toggle */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" />
            Global Configuration
          </CardTitle>
          <CardDescription className="text-xs">
            Controls for B2C connector processing
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">Disable Connectors</Label>
                <p className="text-xs text-muted-foreground">
                  Toggle before B2C code releases to prevent syncs
                </p>
              </div>
              <Switch
                checked={disableConnectors}
                onCheckedChange={handleToggle}
                disabled={toggling}
              />
            </div>
          )}
          {disableConnectors && (
            <div className="mt-3 flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-200">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Connectors are currently disabled — no new syncs will start
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Connectors Insights */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Active Connectors
              </CardTitle>
              <CardDescription className="text-xs">
                Currently running syncs across all organisations
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => {
                setRunningLoading(true);
                loadRunning();
              }}
              disabled={runningLoading}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${runningLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {runningLoading && runningConnectors.length === 0 ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : runningConnectors.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              No connectors are running at the moment.
            </p>
          ) : (
            <div className="space-y-2 max-h-[240px] overflow-y-auto">
              {runningConnectors.map((c) => (
                <div
                  key={`${c.Id}-${c.TriggeredAtUtc}`}
                  className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{c.Name}</span>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">
                        {c.ConnectorType}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {c.OrgName}
                    </p>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatTimeAgo(c.TriggeredAtUtc)}
                      </span>
                    </div>
                    <p className="text-[10px] text-muted-foreground capitalize">
                      {c.TriggerType}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connector Run Health — failing / overdue runs (the accident-only signal) */}
      <ConnectorRunHealthCard
        runHealth={runHealth}
        loading={runHealthLoading}
        onRefresh={() => {
          setRunHealthLoading(true);
          loadRunHealth();
        }}
        onSelectConnector={onSelectConnector}
        className="md:col-span-2"
      />
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

function ConnectorsAdminPage() {
  const { token } = useAuth();

  // Which tab is showing, and (when set) the connector name to pre-filter the
  // All Instances tab by — set when an operator clicks a failing/overdue
  // connector in the run-health panel, so they land on that connector under
  // its owning org. A counter forces a re-seed even if the same name is
  // clicked twice after the operator has since edited the search box.
  const [activeTab, setActiveTab] = useState("types");
  const [instanceFocus, setInstanceFocus] = useState<{
    name: string;
    nonce: number;
  } | null>(null);

  const focusInstance = (connectorName: string) => {
    setInstanceFocus((prev) => ({
      name: connectorName,
      nonce: (prev?.nonce ?? 0) + 1,
    }));
    setActiveTab("instances");
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <PlugIcon className="h-6 w-6" />
            Connectors (Admin)
          </h1>
          <p className="text-muted-foreground">
            Manage connector types, branding, and view all org instances
          </p>
        </div>
      </div>

      {/* Global Configuration & Active Connectors */}
      <GlobalConfigSection token={token} onSelectConnector={focusInstance} />

      {/* Fleet mapping health (referential doctor sweep). Deep-links the same
          way the run-health panel above does — a dangling connector is only
          debuggable once you can get to it. */}
      <MappingHealthCard onSelectConnector={focusInstance} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="types">Connector Types</TabsTrigger>
          <TabsTrigger value="instances">All Instances</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="licence-models">Licence Models</TabsTrigger>
        </TabsList>

        <TabsContent value="types" className="space-y-6 mt-6">
          <ConnectorTypesSection token={token} />
        </TabsContent>

        <TabsContent value="instances" className="space-y-6 mt-6">
          <ConnectorInstancesSection
            token={token}
            focus={instanceFocus}
            onFocusConsumed={() => setInstanceFocus(null)}
          />
        </TabsContent>

        <TabsContent value="subscriptions" className="space-y-6 mt-6">
          <ConnectorSubscriptionsSection token={token} />
        </TabsContent>

        <TabsContent value="licence-models" className="space-y-6 mt-6">
          <LicenceModelsSection token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// =============================================================================
// Connector Types Section
// =============================================================================

function ConnectorTypesSection({ token }: { token: string | null }) {
  const { can } = usePermissions();
  const canEdit = can("edit", "connectors");

  const [showArchived, setShowArchived] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingType, setEditingType] = useState<AdminConnectorType | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newTypeDraft, setNewTypeDraft] = useState<ConnectorTypeCreateRequest>({
    Type: "",
    BatchSize: 100,
    BufferSize: 1000,
    IsSelfService: true,
    RequiresAuthorisation: false,
    IsEarlyStages: false,
    IsInsider: false,
    Status: "ACTIVE",
  });
  const [isCreating, setIsCreating] = useState(false);
  const [registeredTypes, setRegisteredTypes] = useState<
    RegisteredSourceConnectorType[]
  >([]);
  const [registeredTypesLoading, setRegisteredTypesLoading] = useState(false);

  // Template records drill-down sheet — null = closed.
  const [viewingRecordsForType, setViewingRecordsForType] =
    useState<AdminConnectorType | null>(null);
  const [templateRecords, setTemplateRecords] = useState<ConnectorTemplateRecord[]>(
    [],
  );
  const [templateRecordsLoading, setTemplateRecordsLoading] = useState(false);
  const [expandedRecordIds, setExpandedRecordIds] = useState<Set<number>>(
    new Set(),
  );

  // Stats (from full unfiltered count - fetched once)
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    selfService: 0,
    withBranding: 0,
  });

  // Server-side pagination via usePaginatedData
  const fetchTypes = useCallback(
    async (page: number, pageSize: number): Promise<PaginatedResponse<AdminConnectorType>> => {
      if (!token) return { data: [], totalCount: 0, page, pageSize };
      const result = await fetchAdminConnectorTypes(token, {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        search: searchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      return {
        data: result.items,
        totalCount: result.pagination.total,
        page,
        pageSize,
      };
    },
    [token, searchTerm, sortBy, sortOrder],
  );

  const {
    data: types,
    isLoading,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh: refreshTypes,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<AdminConnectorType>({
    fetchFn: fetchTypes,
    dependencies: [token, searchTerm, sortBy, sortOrder],
  });

  // Load template records when the drill-down sheet opens. Kept separate
  // from the usePaginatedData flow because it's a one-shot read against a
  // different endpoint, not a paged table.
  useEffect(() => {
    if (!viewingRecordsForType || !token) return;
    setTemplateRecordsLoading(true);
    setTemplateRecords([]);
    setExpandedRecordIds(new Set());
    fetchConnectorTypeRecords(token, viewingRecordsForType.Id)
      .then(setTemplateRecords)
      .catch(() => setTemplateRecords([]))
      .finally(() => setTemplateRecordsLoading(false));
  }, [viewingRecordsForType, token]);

  // Load the authoritative list of C# engine source connector types when
  // the Add dialog opens. Must live below the `types` destructuring so the
  // dependency array doesn't reference a TDZ variable.
  useEffect(() => {
    if (!isCreateOpen || !token) return;
    setRegisteredTypesLoading(true);
    fetchRegisteredSourceConnectorTypes(token)
      .then((items) => {
        // Filter out types that already have a B2CConnector row. The
        // admin is ADDING a new row; re-picking an existing Type just
        // 409s at submit. Cleaner UX to hide taken options.
        const existingTypes = new Set(
          types.map((t) => t.Type.toLowerCase()),
        );
        const available = items.filter(
          (i) => !existingTypes.has(i.type.toLowerCase()),
        );
        setRegisteredTypes(available);
      })
      .catch(() => setRegisteredTypes([]))
      .finally(() => setRegisteredTypesLoading(false));
  }, [isCreateOpen, token, types]);

  // Load stats once (separate unfiltered call)
  useEffect(() => {
    if (!token) return;
    fetchAdminConnectorTypes(token, { limit: 100, offset: 0 }).then(
      (result) => {
        const all = result.items;
        setStats({
          total: result.pagination.total,
          active: all.filter((t) => t.Status === "ACTIVE").length,
          selfService: all.filter((t) => t.IsSelfService).length,
          withBranding: all.filter((t) => t.AccentColour).length,
        });
      },
    ).catch(() => {});
  }, [token]);

  const displayedTypes = showArchived
    ? types
    : types.filter((t) => t.Status === "ACTIVE");

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }
  function getSortDirection(column: string): "asc" | "desc" | null {
    return sortBy === column ? sortOrder : null;
  }

  async function handleToggleField(
    type: AdminConnectorType,
    field: "IsSelfService" | "IsEarlyStages" | "IsInsider",
    value: boolean,
  ) {
    if (!token) return;
    try {
      await updateConnectorType(token, type.Id, { [field]: value });
      refreshTypes();
    } catch (error) {
      console.error(`Failed to update ${field}:`, error);
      toast.error(`Failed to update ${type.Type}`);
    }
  }

  async function handleToggleStatus(type: AdminConnectorType) {
    if (!token) return;
    const newStatus = type.Status === "ACTIVE" ? "ARCHIVED" : "ACTIVE";
    try {
      await updateConnectorType(token, type.Id, { Status: newStatus });
      toast.success(
        `${type.Type} ${newStatus === "ACTIVE" ? "activated" : "archived"}`,
      );
      refreshTypes();
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error(`Failed to update ${type.Type}`);
    }
  }

  async function handleSaveType(type: AdminConnectorType) {
    if (!token) return;
    setIsSaving(true);
    try {
      const updateData: ConnectorTypeUpdateRequest = {
        AccentColour: type.AccentColour || undefined,
        DisplayName: type.DisplayName || undefined,
        Description: type.Description || undefined,
        IconUrl: type.IconUrl || undefined,
        BatchSize: type.BatchSize,
        BufferSize: type.BufferSize,
        IsSelfService: type.IsSelfService,
        IsEarlyStages: type.IsEarlyStages,
        IsInsider: type.IsInsider,
        // null explicitly clears the column; empty string would trip the
        // API's regex. Undefined omits the field from the PUT so the
        // server's `exclude_none=True` dump skips it.
        TargetTablePrefix: type.TargetTablePrefix || null,
      };
      await updateConnectorType(token, type.Id, updateData);
      toast.success(`Updated ${type.Type}`);
      setEditingType(null);
      refreshTypes();
    } catch (error) {
      console.error("Failed to update connector type:", error);
      toast.error("Failed to update connector type");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleCreateType() {
    if (!token) return;
    const name = (newTypeDraft.Type ?? "").trim();
    if (!name) {
      toast.error("Type is required");
      return;
    }
    setIsCreating(true);
    try {
      const created = await createConnectorType(token, {
        ...newTypeDraft,
        Type: name,
        DisplayName: newTypeDraft.DisplayName?.trim() || undefined,
        AccentColour: newTypeDraft.AccentColour?.trim() || undefined,
        Description: newTypeDraft.Description?.trim() || undefined,
        // Empty string would fail the API regex (must start with a letter);
        // null tells the server to leave the column NULL — the desired
        // state for generic sources where customers supply the prefix.
        TargetTablePrefix:
          newTypeDraft.TargetTablePrefix?.trim() || null,
      });
      toast.success(`Added connector type ${created.Type}`);
      setIsCreateOpen(false);
      setNewTypeDraft({
        Type: "",
        BatchSize: 100,
        BufferSize: 1000,
        IsSelfService: true,
        RequiresAuthorisation: false,
        IsEarlyStages: false,
        IsInsider: false,
        Status: "ACTIVE",
      });
      refreshTypes();
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Failed to add connector type";
      // 409 surfaces from portalFetch as an Error whose message includes
      // "already exists" — show it verbatim so the admin knows to rename.
      toast.error(msg);
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <>
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm("")}
          placeholder="Search by type or name..."
          containerClassName="flex-1 min-w-[200px]"
        />
        <div className="flex items-center space-x-2">
          <Switch
            id="show-archived"
            checked={showArchived}
            onCheckedChange={setShowArchived}
          />
          <Label htmlFor="show-archived">Show archived</Label>
        </div>
        <Button variant="outline" size="sm" onClick={() => refreshTypes()}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
        {canEdit && (
          <Button size="sm" onClick={() => setIsCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add type
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Types</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {stats.active}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Self-Service</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.selfService}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              With Branding
            </CardTitle>
            <Palette className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.withBranding}</div>
          </CardContent>
        </Card>
      </div>

      {/* Connector Types Table */}
      <Card>
        <CardHeader>
          <CardTitle>Connector Types</CardTitle>
          <CardDescription>
            Configure connector type properties, branding, and visibility
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : displayedTypes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <PlugIcon className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                No connector types found
              </h3>
              <p className="text-muted-foreground text-center mb-4">
                {searchTerm
                  ? `No types match "${searchTerm}". Try a different search term.`
                  : "No connector types are available."}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead
                      direction={null}
                      onSort={() => {}}
                      disabled
                      className="w-12"
                    >
                      Colour
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("Type")}
                      onSort={() => handleSort("Type")}
                    >
                      Type
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("DisplayName")}
                      onSort={() => handleSort("DisplayName")}
                    >
                      Display Name
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("Status")}
                      onSort={() => handleSort("Status")}
                    >
                      Status
                    </SortableTableHead>
                    <SortableTableHead
                      direction={null}
                      onSort={() => {}}
                      disabled
                    >
                      Self-Service
                    </SortableTableHead>
                    <SortableTableHead
                      direction={null}
                      onSort={() => {}}
                      disabled
                    >
                      Early Stages
                    </SortableTableHead>
                    <SortableTableHead
                      direction={null}
                      onSort={() => {}}
                      disabled
                    >
                      Insider
                    </SortableTableHead>
                    <SortableTableHead
                      direction={null}
                      onSort={() => {}}
                      disabled
                    >
                      Auth
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("BatchSize")}
                      onSort={() => handleSort("BatchSize")}
                    >
                      Batch
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("BufferSize")}
                      onSort={() => handleSort("BufferSize")}
                    >
                      Buffer
                    </SortableTableHead>
                    <SortableTableHead
                      direction={null}
                      onSort={() => {}}
                      disabled
                    >
                      Spec
                    </SortableTableHead>
                    {canEdit && (
                      <SortableTableHead
                        direction={null}
                        onSort={() => {}}
                        disabled
                        className="text-right"
                      >
                        Actions
                      </SortableTableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedTypes.map((type) => (
                    <TableRow key={type.Id}>
                      <td className="p-4">
                        <ColourSwatch hex={type.AccentColour} />
                      </td>
                      <td className="p-4">
                        <div className="font-medium">{type.Type}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">
                          {type.DisplayName || (
                            <span className="text-muted-foreground italic">
                              {type.Name}
                            </span>
                          )}
                        </div>
                        {type.Description && (
                          <div className="text-xs text-muted-foreground line-clamp-1">
                            {type.Description}
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <Badge
                          variant={
                            type.Status === "ACTIVE" ? "default" : "secondary"
                          }
                          className={canEdit ? "cursor-pointer" : ""}
                          onClick={
                            canEdit
                              ? () => handleToggleStatus(type)
                              : undefined
                          }
                        >
                          {type.Status}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Switch
                          checked={type.IsSelfService ?? false}
                          disabled={!canEdit}
                          onCheckedChange={(checked) =>
                            handleToggleField(type, "IsSelfService", checked)
                          }
                        />
                      </td>
                      <td className="p-4">
                        <Switch
                          checked={type.IsEarlyStages ?? false}
                          disabled={!canEdit}
                          onCheckedChange={(checked) =>
                            handleToggleField(type, "IsEarlyStages", checked)
                          }
                        />
                      </td>
                      <td className="p-4">
                        <Switch
                          checked={type.IsInsider ?? false}
                          disabled={!canEdit}
                          onCheckedChange={(checked) =>
                            handleToggleField(type, "IsInsider", checked)
                          }
                        />
                      </td>
                      <td className="p-4">
                        {type.RequiresAuthorisation ? (
                          <Badge variant="outline">OAuth</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="text-sm">
                          {type.BatchSize ?? "-"}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm">
                          {type.BufferSize ?? "-"}
                        </span>
                      </td>
                      <td className="p-4">
                        {/* The type's provider spec lives on the Drift page, which
                            owns every spec action (baseline / edit / apply). This is
                            a pointer to that row, not a second surface for it. */}
                        <Link
                          to="/portal/platform/schema-drift"
                          hash={`spec-${type.Type}`}
                          className="text-sm underline-offset-2 hover:underline"
                          title="Open this type's provider spec on the Drift page"
                        >
                          Spec
                        </Link>
                      </td>
                      {canEdit && (
                        <td className="p-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewingRecordsForType(type)}
                            title="View template records"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingType({ ...type })}
                            title="Edit connector type"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </td>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {pagination.totalCount > 0 && (
                <div className="mt-4">
                  <Pagination
                    page={pagination.page}
                    totalPages={pagination.totalPages}
                    totalCount={pagination.totalCount}
                    pageSize={pagination.pageSize}
                    pageSizeOptions={pageSizeOptions}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    canGoPrev={canGoPrev}
                    canGoNext={canGoNext}
                    onPageChange={goToPage}
                    onPageSizeChange={setPageSize}
                    onFirstPage={firstPage}
                    onLastPage={lastPage}
                    onPrevPage={prevPage}
                    onNextPage={nextPage}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog
        open={editingType !== null}
        onOpenChange={(open) => !open && setEditingType(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Connector Type</DialogTitle>
            <DialogDescription>
              Update branding and operational settings for {editingType?.Type}
            </DialogDescription>
          </DialogHeader>
          {editingType && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Type (readonly)</Label>
                <Input value={editingType.Type} disabled />
              </div>
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input
                  value={editingType.DisplayName || ""}
                  onChange={(e) =>
                    setEditingType({
                      ...editingType,
                      DisplayName: e.target.value,
                    })
                  }
                  placeholder={editingType.Name}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingType.Description || ""}
                  onChange={(e) =>
                    setEditingType({
                      ...editingType,
                      Description: e.target.value,
                    })
                  }
                  rows={3}
                  placeholder="Brief description of this connector type"
                />
              </div>
              <div className="space-y-2">
                <Label>Accent Colour</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={editingType.AccentColour || "#FFFFFF"}
                    onChange={(e) =>
                      setEditingType({
                        ...editingType,
                        AccentColour: e.target.value.toUpperCase(),
                      })
                    }
                    className="h-10 w-14 cursor-pointer rounded border border-slate-200 p-1"
                  />
                  <span className="text-sm font-mono text-muted-foreground">
                    {editingType.AccentColour || "None"}
                  </span>
                  {editingType.AccentColour && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setEditingType({
                          ...editingType,
                          AccentColour: undefined,
                        })
                      }
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Icon URL</Label>
                <Input
                  value={editingType.IconUrl || ""}
                  onChange={(e) =>
                    setEditingType({
                      ...editingType,
                      IconUrl: e.target.value,
                    })
                  }
                  placeholder="https://..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Batch Size</Label>
                  <Input
                    type="number"
                    value={editingType.BatchSize ?? 0}
                    onChange={(e) =>
                      setEditingType({
                        ...editingType,
                        BatchSize: parseInt(e.target.value) || 0,
                      })
                    }
                    min={0}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Buffer Size</Label>
                  <Input
                    type="number"
                    value={editingType.BufferSize ?? 0}
                    onChange={(e) =>
                      setEditingType({
                        ...editingType,
                        BufferSize: parseInt(e.target.value) || 0,
                      })
                    }
                    min={0}
                  />
                </div>
              </div>
              {/* Target-table prefix. Auto-derived for ClientConnectors
                  when this is set (Xero → X → HaX for Haesloop's Xero);
                  leave blank on generic sources (MySql, SqlServer) so
                  customers type a custom part in the Discover UI. */}
              <div className="space-y-2">
                <Label>Target Table Prefix</Label>
                <Input
                  value={editingType.TargetTablePrefix ?? ""}
                  onChange={(e) =>
                    setEditingType({
                      ...editingType,
                      TargetTablePrefix: e.target.value || null,
                    })
                  }
                  maxLength={8}
                  placeholder="e.g. X, C7, Mf (letters+digits, max 8)"
                  pattern="[A-Za-z][A-Za-z0-9]{0,7}"
                />
                <p className="text-xs text-muted-foreground">
                  Prepended to target table names by the engine. Leave
                  blank for generic sources; customers supply their own
                  custom part at discover time.
                </p>
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Self-Service</Label>
                  <p className="text-sm text-muted-foreground">
                    Users can set up this connector themselves
                  </p>
                </div>
                <Switch
                  checked={editingType.IsSelfService ?? false}
                  onCheckedChange={(checked) =>
                    setEditingType({
                      ...editingType,
                      IsSelfService: checked,
                    })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Early Stages</Label>
                  <p className="text-sm text-muted-foreground">
                    Connector is in early development
                  </p>
                </div>
                <Switch
                  checked={editingType.IsEarlyStages ?? false}
                  onCheckedChange={(checked) =>
                    setEditingType({
                      ...editingType,
                      IsEarlyStages: checked,
                    })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Insider</Label>
                  <p className="text-sm text-muted-foreground">
                    Available only to insider programme members
                  </p>
                </div>
                <Switch
                  checked={editingType.IsInsider ?? false}
                  onCheckedChange={(checked) =>
                    setEditingType({
                      ...editingType,
                      IsInsider: checked,
                    })
                  }
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingType(null)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => editingType && handleSaveType(editingType)}
              disabled={isSaving}
            >
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add new connector type */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add connector type</DialogTitle>
            <DialogDescription>
              The <code>Type</code> must match a registered ISourceConnector in
              the C# refresh engine. Engine support must exist before the new
              type can sync data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="new-type-type">Type</Label>
              <Select
                value={newTypeDraft.Type}
                onValueChange={(value) =>
                  setNewTypeDraft({ ...newTypeDraft, Type: value })
                }
                disabled={registeredTypesLoading || registeredTypes.length === 0}
              >
                <SelectTrigger id="new-type-type">
                  <SelectValue
                    placeholder={
                      registeredTypesLoading
                        ? "Loading types from engine..."
                        : registeredTypes.length === 0
                          ? "No unregistered types available"
                          : "Select a connector type"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {registeredTypes.map((t) => (
                    <SelectItem key={t.type} value={t.type}>
                      {t.type}
                      <span className="ml-2 text-xs text-muted-foreground">
                        {t.className}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Sourced live from the C# engine's <code>/source-connectors/types</code>
                endpoint. Already-registered types are hidden — cancel and use
                Edit instead.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-type-display-name">Display name</Label>
              <Input
                id="new-type-display-name"
                placeholder="Friendly name shown in the portal"
                value={newTypeDraft.DisplayName ?? ""}
                onChange={(e) =>
                  setNewTypeDraft({ ...newTypeDraft, DisplayName: e.target.value })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="new-type-batch">Batch size</Label>
                <Input
                  id="new-type-batch"
                  type="number"
                  min={1}
                  value={newTypeDraft.BatchSize ?? 100}
                  onChange={(e) =>
                    setNewTypeDraft({
                      ...newTypeDraft,
                      BatchSize: Number(e.target.value) || 1,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-type-buffer">Buffer size</Label>
                <Input
                  id="new-type-buffer"
                  type="number"
                  min={1}
                  value={newTypeDraft.BufferSize ?? 1000}
                  onChange={(e) =>
                    setNewTypeDraft({
                      ...newTypeDraft,
                      BufferSize: Number(e.target.value) || 1,
                    })
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-type-accent">Accent colour</Label>
              <Input
                id="new-type-accent"
                placeholder="#4F46E5"
                value={newTypeDraft.AccentColour ?? ""}
                onChange={(e) =>
                  setNewTypeDraft({ ...newTypeDraft, AccentColour: e.target.value })
                }
              />
            </div>
            {/* Same field as the Edit dialog — leave blank for generic
                sources (MySql, SqlServer); set for product types so client
                connectors auto-derive {OrgInitials}{TargetTablePrefix}. */}
            <div className="space-y-2">
              <Label htmlFor="new-type-prefix">Target table prefix</Label>
              <Input
                id="new-type-prefix"
                placeholder="e.g. X, C7, Mf"
                maxLength={8}
                pattern="[A-Za-z][A-Za-z0-9]{0,7}"
                value={newTypeDraft.TargetTablePrefix ?? ""}
                onChange={(e) =>
                  setNewTypeDraft({
                    ...newTypeDraft,
                    TargetTablePrefix: e.target.value || null,
                  })
                }
              />
              <p className="text-xs text-muted-foreground">
                Optional. Letters + digits, max 8. Leave blank for generic
                sources where the customer supplies the prefix.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center space-x-2 text-sm">
                <Switch
                  checked={newTypeDraft.IsSelfService ?? true}
                  onCheckedChange={(v) =>
                    setNewTypeDraft({ ...newTypeDraft, IsSelfService: v })
                  }
                />
                <span>Self-service</span>
              </label>
              <label className="flex items-center space-x-2 text-sm">
                <Switch
                  checked={newTypeDraft.RequiresAuthorisation ?? false}
                  onCheckedChange={(v) =>
                    setNewTypeDraft({ ...newTypeDraft, RequiresAuthorisation: v })
                  }
                />
                <span>Requires auth</span>
              </label>
              <label className="flex items-center space-x-2 text-sm">
                <Switch
                  checked={newTypeDraft.IsEarlyStages ?? false}
                  onCheckedChange={(v) =>
                    setNewTypeDraft({ ...newTypeDraft, IsEarlyStages: v })
                  }
                />
                <span>Early stages</span>
              </label>
              <label className="flex items-center space-x-2 text-sm">
                <Switch
                  checked={newTypeDraft.IsInsider ?? false}
                  onCheckedChange={(v) =>
                    setNewTypeDraft({ ...newTypeDraft, IsInsider: v })
                  }
                />
                <span>Insider</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateType} disabled={isCreating}>
              {isCreating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template records drill-down */}
      <Sheet
        open={viewingRecordsForType !== null}
        onOpenChange={(open) => {
          if (!open) setViewingRecordsForType(null);
        }}
      >
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {viewingRecordsForType
                ? `${viewingRecordsForType.Type} · template records`
                : "Template records"}
            </SheetTitle>
            <SheetDescription>
              Platform templates stored in <code>B2CConnectorRecord</code>. These
              are what the C# engine syncs when a client connector has no
              per-instance records seeded (i.e. every connector type except
              the generic SQL sources).
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-2">
            {templateRecordsLoading ? (
              <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading records…
              </div>
            ) : templateRecords.length === 0 ? (
              <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
                No template records for this connector type. Generic source
                types (SqlServer, MySql, FTP, OData) define their records
                per-client via the Discover Tables flow — nothing here by design.
              </div>
            ) : (
              templateRecords.map((rec) => {
                const expanded = expandedRecordIds.has(rec.Id);
                return (
                  <div
                    key={rec.Id}
                    className="rounded-md border border-slate-200 bg-white"
                  >
                    <button
                      type="button"
                      className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() =>
                        setExpandedRecordIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(rec.Id)) next.delete(rec.Id);
                          else next.add(rec.Id);
                          return next;
                        })
                      }
                    >
                      {expanded ? (
                        <ChevronDown className="mt-0.5 h-4 w-4 flex-none text-slate-500" />
                      ) : (
                        <ChevronRight className="mt-0.5 h-4 w-4 flex-none text-slate-500" />
                      )}
                      <div className="min-w-0 flex-auto">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium text-slate-900">
                            {rec.Name}
                          </span>
                          {rec.ParentId !== null && (
                            <Badge variant="outline" className="text-[10px]">
                              child
                            </Badge>
                          )}
                          {rec.Disabled && (
                            <Badge variant="outline" className="text-[10px] text-amber-700">
                              disabled
                            </Badge>
                          )}
                          {rec.Paged && (
                            <Badge variant="outline" className="text-[10px]">
                              paged
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {rec.Source && <span>source: {rec.Source}</span>}
                          {rec.Identifier && (
                            <span className="ml-3">id: {rec.Identifier}</span>
                          )}
                          <span className="ml-3">{rec.Properties.length} props</span>
                        </div>
                      </div>
                    </button>
                    {expanded && (
                      <div className="border-t border-slate-100 px-3 py-2">
                        {rec.Properties.length === 0 ? (
                          <p className="text-xs text-muted-foreground">
                            No properties defined.
                          </p>
                        ) : (
                          <ul className="divide-y divide-slate-100 text-xs">
                            {rec.Properties.map((p) => (
                              <li key={p.Id} className="py-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono font-medium text-slate-900">
                                    {p.Name}
                                  </span>
                                  {p.Primary && (
                                    <Badge variant="outline" className="text-[10px]">
                                      PK
                                    </Badge>
                                  )}
                                  {p.Type && (
                                    <span className="text-muted-foreground">
                                      {p.Type}
                                      {p.Length ? `(${p.Length})` : null}
                                      {p.Precision && p.Scale !== null
                                        ? `(${p.Precision},${p.Scale})`
                                        : null}
                                    </span>
                                  )}
                                  {p.Nullable === false && (
                                    <span className="text-muted-foreground">
                                      NOT NULL
                                    </span>
                                  )}
                                </div>
                                {p.Source && p.Source !== p.Name && (
                                  <div className="text-[11px] text-muted-foreground">
                                    source: {p.Source}
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// =============================================================================
// Connector Instances Section
// =============================================================================

const STATUS_OPTIONS = ["", "ACTIVE", "DELETED", "DISCONNECTED"] as const;

function daysRemaining(deletedAtUtc: string): number {
  const deletedAt = new Date(deletedAtUtc);
  const retainedUntil = new Date(
    deletedAt.getTime() + 60 * 24 * 60 * 60 * 1000,
  );
  return Math.max(
    0,
    Math.ceil((retainedUntil.getTime() - Date.now()) / (24 * 60 * 60 * 1000)),
  );
}

function ConnectorInstancesSection({
  token,
  focus,
  onFocusConsumed,
}: {
  token: string | null;
  focus?: { name: string; nonce: number } | null;
  onFocusConsumed?: () => void;
}) {
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // When the run-health panel deep-links to a specific connector, seed the
  // search box with its name (and clear any status/type filters that could
  // hide it) so it surfaces under its owning org. Keyed on the focus nonce so
  // repeated clicks re-seed even after the operator has edited the box. The
  // focus is a one-shot — consumed immediately — so that manually reopening
  // the tab later (Radix remounts this section) starts from a clean search
  // rather than re-applying the last deep-link.
  const searchInputRef = useRef<HTMLInputElement>(null);
  const focusNonce = focus?.nonce ?? null;
  useEffect(() => {
    if (!focus) return;
    setSearchTerm(focus.name);
    setStatusFilter("");
    setTypeFilter("");
    onFocusConsumed?.();
    // Move keyboard focus to the (now-seeded) search box so a screen-reader /
    // keyboard operator lands on the filtered results rather than being left on
    // the run-health button in a different part of the page.
    searchInputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusNonce]);

  // Promote-to-type dialog state — null = closed.
  // The "Promote" row action lifts the per-client B2CClientConnectorRecord
  // rows into a new B2CConnector type with BaseTransport set so the C#
  // engine's alias mechanism resolves the new Type to an existing class.
  //
  // BU35: HIDDEN in prod. The dialog POSTs `/connectors/{id}/promote-to-type`
  // + `/source-types/reload-aliases`, but neither endpoint exists on b2c main
  // (the backend lived on `feat/promote-to-type`, which has since been deleted
  // from the remote), so clicking Promote 404s. Flip this to true once the
  // backend endpoints land. The dialog + eligibility code is retained so
  // re-enabling is a one-line change.
  const PROMOTE_TO_TYPE_ENABLED = false;
  const [promoteTarget, setPromoteTarget] =
    useState<AdminConnectorInstance | null>(null);

  // Promote-to-type eligibility lives in ./promotable.ts so it's
  // unit-testable — see promotable.test.ts.

  // Stats (from pagination total, updated with each load)
  const [statusCounts, setStatusCounts] = useState({
    total: 0,
    ACTIVE: 0,
    DELETED: 0,
    DISCONNECTED: 0,
  });

  // Unique types for filter dropdown (fetched once)
  const [uniqueTypes, setUniqueTypes] = useState<string[]>([]);

  // Server-side pagination via usePaginatedData
  const fetchInstances = useCallback(
    async (page: number, pageSize: number): Promise<PaginatedResponse<AdminConnectorInstance>> => {
      if (!token) return { data: [], totalCount: 0, page, pageSize };
      const result = await fetchAdminConnectorInstances(token, {
        status: statusFilter || undefined,
        connectorType: typeFilter || undefined,
        search: searchTerm || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      return {
        data: result.items,
        totalCount: result.pagination.total,
        page,
        pageSize,
      };
    },
    [token, statusFilter, typeFilter, searchTerm, sortBy, sortOrder],
  );

  const {
    data: instances,
    isLoading,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh: refreshInstances,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<AdminConnectorInstance>({
    fetchFn: fetchInstances,
    dependencies: [token, statusFilter, typeFilter, searchTerm, sortBy, sortOrder],
  });

  // Fetch stats and unique types once on mount
  useEffect(() => {
    if (!token) return;
    // Fetch a larger batch without filters to get stats + unique types
    fetchAdminConnectorInstances(token, { limit: 1, offset: 0 }).then(
      (result) => {
        setStatusCounts((prev) => ({ ...prev, total: result.pagination.total }));
      },
    ).catch(() => {});
    // Get counts per status
    for (const status of ["ACTIVE", "DELETED", "DISCONNECTED"] as const) {
      fetchAdminConnectorInstances(token, {
        status,
        limit: 1,
        offset: 0,
      }).then((result) => {
        setStatusCounts((prev) => ({
          ...prev,
          [status]: result.pagination.total,
        }));
      }).catch(() => {});
    }
    // Walk every page (server limit caps at 100) and collect the union
    // of ConnectorType values. Was a single limit=100 fetch — silently
    // hid types whose first instance fell past row 100 (MySql, FTP,
    // etc.) so admins couldn't filter to them.
    (async () => {
      const types = new Set<string>();
      let offset = 0;
      const pageSize = 100;
      // Hard cap iterations defensively in case server returns wrong
      // pagination metadata (50 pages × 100 = 5000 instances).
      for (let i = 0; i < 50; i++) {
        const page = await fetchAdminConnectorInstances(token, {
          limit: pageSize,
          offset,
        });
        for (const inst of page.items) types.add(inst.ConnectorType);
        if (page.items.length < pageSize) break;
        offset += pageSize;
      }
      setUniqueTypes(Array.from(types).sort());
    })().catch(() => {});
  }, [token]);

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }
  function getSortDirection(column: string): "asc" | "desc" | null {
    return sortBy === column ? sortOrder : null;
  }

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Instances
            </CardTitle>
            <PlugZap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statusCounts.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {statusCounts.ACTIVE}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Deleted</CardTitle>
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-rose-600">
              {statusCounts.DELETED}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Disconnected</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {statusCounts.DISCONNECTED}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content panel */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            ref={searchInputRef}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search by org or connector name..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Select
            value={statusFilter || "all"}
            onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s || "all"} value={s || "all"}>
                  {s || "All statuses"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={typeFilter || "all"}
            onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {uniqueTypes.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => refreshInstances()}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : instances.length === 0 ? (
          <div className="text-center py-12">
            <PlugZap className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm || statusFilter || typeFilter
                ? "No instances match your filters"
                : "No connector instances yet"}
            </h3>
            <p className="text-muted-foreground">
              {searchTerm || statusFilter || typeFilter
                ? "Try a different search term or clear the filters."
                : "Connector instances will appear here once organisations create them."}
            </p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    direction={getSortDirection("org_name")}
                    onSort={() => handleSort("org_name")}
                  >
                    Organisation
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("name")}
                    onSort={() => handleSort("name")}
                  >
                    Connector Name
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("connector_type")}
                    onSort={() => handleSort("connector_type")}
                  >
                    Type
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("connection")}
                    onSort={() => handleSort("connection")}
                  >
                    Connection
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("status")}
                    onSort={() => handleSort("status")}
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead direction={null} onSort={() => {}} disabled>
                    Schedule
                  </SortableTableHead>
                  <SortableTableHead direction={null} onSort={() => {}} disabled>
                    Deleted At
                  </SortableTableHead>
                  <SortableTableHead direction={null} onSort={() => {}} disabled>
                    Retention
                  </SortableTableHead>
                  <SortableTableHead direction={null} onSort={() => {}} disabled>
                    Actions
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {instances.map((instance, idx) => (
                  <TableRow key={instance.Id}>
                    <td className="p-4">
                      <div className="font-medium">{instance.OrgName}</div>
                      <div className="text-xs text-muted-foreground">
                        {instance.OrgKey}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="font-medium">{instance.Name}</div>
                      <div className="text-xs text-muted-foreground">
                        ID: {instance.Id}
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline">{instance.ConnectorType}</Badge>
                    </td>
                    <td className="p-4">
                      <ConnectionCell
                        instance={instance}
                        previous={instances[idx - 1]}
                      />
                    </td>
                    <td className="p-4">
                      <Badge
                        variant={
                          instance.Status === "ACTIVE"
                            ? "default"
                            : instance.Status === "DELETED"
                              ? "destructive"
                              : "secondary"
                        }
                      >
                        {instance.Status}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <span className="text-sm">
                        {instance.Schedule || (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </span>
                    </td>
                    <td className="p-4">
                      {instance.DeletedAtUtc ? (
                        <span className="text-sm">
                          {new Date(instance.DeletedAtUtc).toLocaleDateString(
                            "en-ZA",
                            { dateStyle: "medium" },
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-4">
                      {instance.DeletedAtUtc ? (
                        <RetentionBadge deletedAtUtc={instance.DeletedAtUtc} />
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-4">
                      {/* Promote only shown for instances whose type is
                          a generic transport (MySql/SqlServer/FTP) and
                          status is ACTIVE. API connectors (Xero, Cin7,
                          …) don't go through the seed-from-source flow
                          so there's nothing to lift. The API still
                          rejects edge cases (already-aliased, no
                          records, etc.) and surfaces them in the
                          dialog — UI just hides the obviously-wrong
                          ones. */}
                      {PROMOTE_TO_TYPE_ENABLED &&
                      isPromotableTransport(instance.ConnectorType) ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPromoteTarget(instance)}
                          disabled={instance.Status !== "ACTIVE"}
                        >
                          Promote
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {pagination.totalCount > 0 && (
              <div className="mt-4 border-t pt-4">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  pageSizeOptions={pageSizeOptions}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  canGoPrev={canGoPrev}
                  canGoNext={canGoNext}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  onFirstPage={firstPage}
                  onLastPage={lastPage}
                  onPrevPage={prevPage}
                  onNextPage={nextPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      <PromoteToTypeDialog
        open={promoteTarget !== null}
        onClose={() => setPromoteTarget(null)}
        token={token}
        connectorId={promoteTarget?.Id ?? 0}
        connectorName={promoteTarget?.Name ?? ""}
        orgName={promoteTarget?.OrgName ?? ""}
        currentType={promoteTarget?.ConnectorType ?? ""}
        onPromoted={() => refreshInstances()}
      />
    </>
  );
}


// =============================================================================
// Connector Subscriptions Section
// =============================================================================

function ConnectorSubscriptionsSection({ token }: { token: string | null }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [sortBy, setSortBy] = useState("organisation_name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const fetchSubs = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<Subscription>> => {
      if (!token) return { data: [], totalCount: 0, page, pageSize };
      const result = await platformApi.listSubscriptions(token, {
        category: "connector",
        status: statusFilter === "all" ? undefined : statusFilter,
        search: searchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      return {
        data: result.items || [],
        totalCount: result.total ?? 0,
        page,
        pageSize,
      };
    },
    [token, statusFilter, searchTerm, sortBy, sortOrder],
  );

  const {
    data: subs,
    isLoading,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh: refreshSubs,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<Subscription>({
    fetchFn: fetchSubs,
    dependencies: [token, statusFilter, searchTerm, sortBy, sortOrder],
  });

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }
  function getSortDirection(column: string): "asc" | "desc" | null {
    return sortBy === column ? sortOrder : null;
  }

  return (
    <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
      {/* Controls bar */}
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm("")}
          placeholder="Search by service or organisation..."
          containerClassName="flex-1 min-w-[200px]"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => refreshSubs()}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
      </div>

      {isLoading && subs.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : subs.length === 0 ? (
        <div className="text-center py-12">
          <PlugZap className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">
            {searchTerm || statusFilter !== "all"
              ? "No subscriptions match your filters"
              : "No connector subscriptions yet"}
          </h3>
          <p className="text-muted-foreground">
            {searchTerm || statusFilter !== "all"
              ? "Try a different search term or clear the filters."
              : "Connector subscriptions will appear here when organisations subscribe."}
          </p>
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  direction={getSortDirection("organisation_name")}
                  onSort={() => handleSort("organisation_name")}
                >
                  Organisation
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("service_name")}
                  onSort={() => handleSort("service_name")}
                >
                  Service
                </SortableTableHead>
                <SortableTableHead direction={null} onSort={() => {}} disabled>
                  Instance ID
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("tier")}
                  onSort={() => handleSort("tier")}
                >
                  Tier
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("status")}
                  onSort={() => handleSort("status")}
                >
                  Status
                </SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subs.map((sub, idx) => (
                <TableRow
                  key={`${sub.organisation_id}-${sub.service_name}-${sub.client_connector_id ?? idx}`}
                >
                  <td className="p-3 text-sm">
                    {sub.organisation_name || sub.organisation_id}
                  </td>
                  <td className="p-3 text-sm font-medium">
                    {sub.service_display_name || sub.service_name}
                  </td>
                  <td className="p-3 text-sm">
                    {sub.client_connector_id ? (
                      <Badge variant="outline">#{sub.client_connector_id}</Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-3 text-sm">
                    <Badge variant="outline">{sub.tier}</Badge>
                  </td>
                  <td className="p-3 text-sm">
                    <Badge
                      variant={
                        sub.status.toLowerCase() === "active"
                          ? "success"
                          : "destructive"
                      }
                    >
                      {sub.status}
                    </Badge>
                  </td>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {pagination.totalCount > 0 && (
            <div className="mt-4 border-t pt-4">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                pageSize={pagination.pageSize}
                pageSizeOptions={pageSizeOptions}
                startIndex={startIndex}
                endIndex={endIndex}
                canGoPrev={canGoPrev}
                canGoNext={canGoNext}
                onPageChange={goToPage}
                onPageSizeChange={setPageSize}
                onFirstPage={firstPage}
                onLastPage={lastPage}
                onPrevPage={prevPage}
                onNextPage={nextPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}


function RetentionBadge({ deletedAtUtc }: { deletedAtUtc: string }) {
  const days = daysRemaining(deletedAtUtc);
  if (days <= 0) {
    return <Badge variant="destructive">Expired</Badge>;
  }
  if (days <= 7) {
    return (
      <Badge variant="destructive">
        {days}d remaining
      </Badge>
    );
  }
  return (
    <Badge variant="secondary">
      {days}d remaining
    </Badge>
  );
}

// =============================================================================
// Licence Models Section (read-only drill-down)
// =============================================================================
function LicenceModelsSection({ token }: { token: string | null }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [models, setModels] = useState<LicenceModel[]>([]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchLicenceModels(token);
      setModels(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load licence models");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalClients = useMemo(
    () => models.reduce((sum, m) => sum + m.ClientCount, 0),
    [models],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Licence models</CardTitle>
        <CardDescription>
          The <code>b2c.B2CLicenceModel</code> rows that gate what the C# refresh
          engine syncs. Read-only — attaching a client to a model is still a
          manual operator step (<code>B2CLicenceModelClient</code>); CP's
          subscription tier does not yet auto-populate this.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-3 text-sm">
          <Button variant="outline" size="sm" onClick={() => void load()}>
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
          <span className="text-muted-foreground">
            {models.length} models · {totalClients} client attachments
          </span>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {loading && models.length === 0 && (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading licence models…
          </div>
        )}

        {!loading && models.length === 0 && !error && (
          <p className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
            No licence models found.
          </p>
        )}

        {models.map((m) => {
          const isOpen = expanded.has(m.Id);
          const totalRecords = m.Connectors.reduce(
            (sum, c) => sum + c.Records.length,
            0,
          );
          return (
            <div
              key={m.Id}
              className="rounded-md border border-slate-200 bg-white"
            >
              <button
                type="button"
                className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-slate-50"
                onClick={() =>
                  setExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(m.Id)) next.delete(m.Id);
                    else next.add(m.Id);
                    return next;
                  })
                }
                aria-expanded={isOpen}
              >
                {isOpen ? (
                  <ChevronDown className="mt-0.5 h-4 w-4 flex-none text-slate-500" />
                ) : (
                  <ChevronRight className="mt-0.5 h-4 w-4 flex-none text-slate-500" />
                )}
                <div className="min-w-0 flex-auto">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">
                      {m.Description ?? `model ${m.Id}`}
                    </span>
                    {m.Base && (
                      <Badge variant="outline" className="text-[10px]">
                        base
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {m.ClientCount} clients · {m.Connectors.length} connector
                    {m.Connectors.length === 1 ? "" : "s"} · {totalRecords}{" "}
                    record{totalRecords === 1 ? "" : "s"}
                  </div>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-slate-100 px-3 py-2 space-y-2">
                  {m.Connectors.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No connectors entitled under this model.
                    </p>
                  ) : (
                    m.Connectors.map((c) => (
                      <div
                        key={c.ConnectorId}
                        className="rounded border border-slate-100 p-2"
                      >
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-mono font-medium text-slate-900">
                            {c.Type}
                          </span>
                          {c.IsDefault && (
                            <Badge variant="outline" className="text-[10px]">
                              default
                            </Badge>
                          )}
                          <span className="text-muted-foreground">
                            {c.Records.length} record
                            {c.Records.length === 1 ? "" : "s"}
                          </span>
                        </div>
                        {c.Records.length > 0 && (
                          <ul className="mt-1 ml-4 list-disc text-[11px] text-muted-foreground">
                            {c.Records.map((r) => (
                              <li key={r.Id} className="font-mono">
                                {r.Name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
